import { Phone, MessageCircle } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation, buildOrderContactLink } from '@dukanti/shared';
import type { Order } from '@dukanti/shared';

export interface OrderDetailCustomerProps {
  readonly order: Order;
}

/**
 * F-022: Customer info section for the order detail page.
 *
 * Shows the customer name and, when a phone number is present, call and
 * WhatsApp contact buttons. `buildOrderContactLink` returns `string | null` —
 * buttons are hidden when the result is null (invalid format or no phone).
 */
export function OrderDetailCustomer({ order }: OrderDetailCustomerProps): React.JSX.Element {
  const { t, lang, i18n } = useTranslation();

  // `buildOrderContactLink` expects i18next's raw `TFunction`. Our shared
  // `useTranslation` exposes a narrowed wrapper `t`; we pass `i18n.t` bound
  // to the instance so the signature matches exactly — same pattern as
  // `use-contact-seller-handler.ts`.
  const rawT = i18n.t.bind(i18n) as TFunction;

  // Compute contact links outside render expressions for testability.
  // Returns null when phone is absent or format is invalid (E.164 guard).
  const callHref =
    order.customer_phone_snapshot != null
      ? buildOrderContactLink({
          method: 'phone',
          number: order.customer_phone_snapshot,
          orderId: order.id,
          locale: lang as 'ar' | 'he' | 'en',
          t: rawT,
        })
      : null;

  const whatsappHref =
    order.customer_phone_snapshot != null
      ? buildOrderContactLink({
          method: 'whatsapp',
          number: order.customer_phone_snapshot,
          orderId: order.id,
          locale: lang as 'ar' | 'he' | 'en',
          t: rawT,
        })
      : null;

  return (
    <section
      data-testid="order-detail-customer"
      className="border-border bg-card shadow-admin-card rounded-lg border p-6"
    >
      <h2 className="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wide">
        {t('admin.orders.detail.customer')}
      </h2>

      <p data-testid="order-customer-name" className="text-foreground mb-4 font-medium">
        {order.customer_name_snapshot}
      </p>

      {(callHref != null || whatsappHref != null) && (
        <div className="flex flex-row flex-wrap gap-2">
          {callHref != null && (
            <a
              href={callHref}
              data-testid="order-customer-call"
              className="border-border hover:bg-admin-surface-muted inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium"
            >
              <Phone aria-hidden="true" className="size-4" strokeWidth={1.75} />
              <span>{t('admin.orders.detail.callButton')}</span>
            </a>
          )}

          {whatsappHref != null && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-customer-whatsapp"
              className="border-border hover:bg-admin-surface-muted inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium"
            >
              <MessageCircle aria-hidden="true" className="size-4" strokeWidth={1.75} />
              <span>{t('admin.orders.detail.whatsappButton')}</span>
            </a>
          )}
        </div>
      )}
    </section>
  );
}
